import React, {useEffect, useMemo, useState} from "react";
import { useTranslation } from "react-i18next";
import {
    FiArrowLeft,
    FiCalendar, FiCheckCircle,
    FiChevronDown, FiChevronRight,
    FiClock, FiDownload, FiEye, FiList, FiPercent, FiStopCircle,
    FiUser,
} from "react-icons/fi";
import {useNavigate, useParams} from "react-router-dom";
import PageHeader from "../../../components/common/pageheader/PageHeader";
import {
    downloadQuestionAnalysisExcel,
    finishExam,
    getExamById,
    getExamStudents,
    getQuestionAnalysisGroup,
} from "../../../services/examsApi";
import {useAuth} from "../../../context/AuthContext";
import ConfirmActionModal from "../../Settings/components/ConfirmActionModal";
import TableEmptyState from "../../../components/common/tableEmptyState/TableEmptyState";
import styles from "./ExamDetailPage.module.css";
import Loader from "../../../components/common/Loader/Loader";
import {toStatusAz} from "../../../utils/statusText";
import {renderAttemptStatusBadge, renderFlagBadge} from "../../../utils/statusUtil";
import {confirmAllExamAttempts, downloadExamAttemptsExcel, getExamAttempts} from "../../../services/examAttemptsApi";
import ConfirmCancelModal from "../../../components/common/cancelModal/ConfirmCancelModal";
import {toast} from "react-toastify";
import {formatDuration, truncate} from "../../../utils/commonUtil";
import {formatDateTime} from "../../../utils/formatDateTime";
import {AiFillQuestionCircle} from "react-icons/ai";
import {BsFillQuestionCircleFill} from "react-icons/bs";
import {getDifficulty} from "../../../utils/difficulty.util";
import {groupKey, useLazyGroups} from "../../../hooks/useLazyGroups";

const PAGE_SIZE = 10;

// Score percentages to one decimal. The average comes straight out of a SQL AVG(), so it arrives as
// a full-precision double (21.371333333333375) that has no business being shown as-is.
const formatScore = (value) =>
    value === null || value === undefined || Number.isNaN(Number(value)) ? "-" : Number(value).toFixed(1);

/** Shared pager for the two big tables. Hidden when everything already fits on one page. */
const TablePager = ({page, totalPages, onChange, busy, t}) => {
    if (totalPages <= 1) return null;
    return (
        <div className={styles.pager}>
            <button type="button" onClick={() => onChange(page - 1)} disabled={busy || page <= 1}>
                {t("common.previous")}
            </button>
            <span className={styles.pagerLabel}>{t("common.pageOf", {page, total: totalPages})}</span>
            <button type="button" onClick={() => onChange(page + 1)} disabled={busy || page >= totalPages}>
                {t("common.next2")}
            </button>
        </div>
    );
};

const ExamDetailPage = () => {
    const { t } = useTranslation();
    const {id} = useParams();
    const navigate = useNavigate();
    const examId = Number(id);

    const [exam, setExam] = useState(null);
    const [examBase, setExamBase] = useState(null);
    const [examSummary, setExamSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // Collapsed subject → topic headers only; each group's question rows load on open.
    const [analysisGroups, setAnalysisGroups] = useState([]);

    // Whole-exam figures. They come from GET /exams/{id}, which aggregates over every row in the
    // database — paging either table below never changes them.
    const [studentCount, setStudentCount] = useState(0);
    const [confirmableAttemptCount, setConfirmableAttemptCount] = useState(0);

    // The two big tables are paged independently of each other.
    const [students, setStudents] = useState([]);
    const [studentsPage, setStudentsPage] = useState(1);
    const [studentsTotalPages, setStudentsTotalPages] = useState(1);
    const [studentsLoading, setStudentsLoading] = useState(false);

    const [attempts, setAttempts] = useState([]);
    const [attemptsPage, setAttemptsPage] = useState(1);
    const [attemptsTotal, setAttemptsTotal] = useState(0);
    const [attemptsTotalPages, setAttemptsTotalPages] = useState(1);
    const [attemptsLoading, setAttemptsLoading] = useState(false);

    const [confirming, setConfirming] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [showConfirmResultModal, setShowConfirmResultModal] = useState(false);

    const {user} = useAuth() || {};
    const isStudent = (user?.roles || []).includes("STUDENT");

    const [showFinishModal, setShowFinishModal] = useState(false);
    const [finishing, setFinishing] = useState(false);

    const handleExportAttempts = async () => {
        if (exporting || !examId) return;
        try {
            setExporting(true);
            await downloadExamAttemptsExcel({examId});
        } catch (e) {
            toast.error(t("pages.examAttempts.exportError", {defaultValue: "İxrac alınmadı"}));
        } finally {
            setExporting(false);
        }
    };

    const [exportingQA, setExportingQA] = useState(false);
    const handleExportQuestionAnalysis = async () => {
        if (exportingQA || !examId) return;
        try {
            setExportingQA(true);
            await downloadQuestionAnalysisExcel(examId);
        } catch (e) {
            toast.error(t("pages.examDetail.questionAnalyticsExportError", {defaultValue: "İxrac alınmadı"}));
        } finally {
            setExportingQA(false);
        }
    };

    // Exam-level payload: the header cards, the whole-exam summary and the per-question analytics.
    // None of it is affected by the paging of the tables below.
    const fetchExam = async () => {
        setLoading(true);
        setError("");
        try {
            const data = await getExamById(examId);
            setExam(data.exam || null);
            setExamBase(data.examBase || null);
            setExamSummary(data.examSummary);
            setAnalysisGroups(Array.isArray(data.questionAnalysisGroups) ? data.questionAnalysisGroups : []);
            setStudentCount(data.studentCount || 0);
            setConfirmableAttemptCount(data.confirmableAttemptCount || 0);
        } catch (e) {
            setError(t("pages.examDetail.loadError"));
        } finally {
            setLoading(false);
        }
    };

    const fetchStudents = async (page = studentsPage) => {
        setStudentsLoading(true);
        try {
            const data = await getExamStudents(examId, {pageNo: page, pageSize: PAGE_SIZE});
            setStudents(Array.isArray(data?.content) ? data.content : []);
            setStudentsTotalPages(data?.totalPages || 1);
        } catch (e) {
            setError(t("pages.examDetail.loadError"));
        } finally {
            setStudentsLoading(false);
        }
    };

    const fetchAttempts = async (page = attemptsPage, {silent = false} = {}) => {
        if (!silent) setAttemptsLoading(true);
        try {
            const data = await getExamAttempts({examId: Number(examId), pageNo: page, pageSize: PAGE_SIZE});
            setAttempts(Array.isArray(data?.content) ? data.content : []);
            setAttemptsTotal(data?.totalElements || 0);
            setAttemptsTotalPages(data?.totalPages || 1);
        } catch (e) {
            // A failed silent refresh keeps showing the last good data instead of an error banner.
            if (!silent) setError(t("pages.examDetail.attemptsLoadError"));
        } finally {
            if (!silent) setAttemptsLoading(false);
        }
    };

    const onConfirm = async () => {
        setConfirming(true);
        try {
            await confirmAllExamAttempts(examId);
            toast.success(t("pages.examDetail.confirmSuccess"));
            // Refetch the exam too: confirming changes the global confirmable count behind the button.
            await Promise.all([fetchExam(), fetchAttempts(attemptsPage)]);
        } catch (e) {
            setError(t("pages.examDetail.confirmError"));
        } finally {
            setConfirming(false);
            setShowConfirmResultModal(false);
        }
    }

    const handleFinishExam = async () => {
        if (finishing) return;
        setFinishing(true);
        try {
            await finishExam(examId);
            toast.success(t("pages.examDetail.finishSuccess", {defaultValue: "İmtahan uğurla bitirildi"}));
            setShowFinishModal(false);
            await fetchExam();
            await fetchAttempts();
        } catch (e) {
            const msg = e?.response?.data?.message || e?.response?.data?.error || t("pages.examDetail.finishError", {defaultValue: "İmtahan bitirilərkən xəta baş verdi"});
            toast.error(msg);
        } finally {
            setFinishing(false);
        }
    }

    useEffect(() => {
        if (!Number.isNaN(examId)) {
            fetchExam();
        }
    }, [examId]);

    useEffect(() => {
        if (!Number.isNaN(examId)) fetchStudents(studentsPage);
    }, [examId, studentsPage]);

    useEffect(() => {
        if (!Number.isNaN(examId)) fetchAttempts(attemptsPage);
    }, [examId, attemptsPage]);

    // While the exam is running, refresh the attempts silently so the "last activity" column and
    // statuses stay current without the teacher reloading. Skipped while the tab is hidden.
    useEffect(() => {
        if (exam?.status !== "STARTED") return;
        const id = window.setInterval(() => {
            if (document.hidden) return;
            fetchAttempts(attemptsPage, {silent: true});
        }, 30000);
        return () => window.clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [exam?.status, attemptsPage, examId]);

    const renderStatusBadge = (status) => {
        if (!status) return "-";
        const normalized = String(status).toUpperCase();
        const className =
            normalized === "ACTIVE"
                ? styles.statusActive
                : normalized === "CANCELLED"
                    ? styles.statusCancelled
                    : styles.statusDefault;
        return <span className={className}>{toStatusAz(normalized)}</span>;
    };

    // "Last seen" for live attempts: when the student's browser last reached the server (autosave,
    // proctoring photo, start/resume). Red means quiet for 3+ minutes — likely a lost connection,
    // though an unproctored student sitting on one question also goes quiet; there is no heartbeat
    // to tell those apart. Values refresh with the 30s silent poll while the exam is running.
    const renderLastSeen = (attempt) => {
        const live = attempt.attemptStatus === "IN_PROGRESS" || attempt.attemptStatus === "RE_ENTER";
        if (!live || !attempt.lastActivityAt) return "-";
        const ts = new Date(attempt.lastActivityAt).getTime();
        if (Number.isNaN(ts)) return "-";
        const seconds = Math.max(0, Math.floor((Date.now() - ts) / 1000));
        const label = seconds < 60
            ? t("common.timeJustNow")
            : seconds < 3600
                ? t("common.timeMinutesAgo", {count: Math.floor(seconds / 60)})
                : t("common.timeHoursAgo", {count: Math.floor(seconds / 3600)});
        const color = seconds <= 120 ? "#10b981" : seconds <= 180 ? "#f59e0b" : "#dc2626";
        return (
            <span
                style={{display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap"}}
                title={seconds > 180 ? t("pages.examDetail.maybeDisconnected") : undefined}
            >
                <span style={{width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0}}/>
                {label}
            </span>
        );
    };

    const renderStudentExamResultStatusBadge = (status) => {
        if (!status) return "-";
        const normalized = String(status).toUpperCase();
        const className =
            normalized === "PASS"
                ? styles.statusActive
                : normalized === "FAIL"
                    ? styles.statusCancelled
                    : styles.statusDefault;
        return <span className={className}>{toStatusAz(normalized)}</span>;
    };

    // Counted server-side across the whole exam (and against exactly the statuses confirm-all
    // updates), so the button doesn't appear or vanish depending on which page of attempts is shown.
    const canBeConfirmedAll = () => confirmableAttemptCount > 0;

    const goBack = () => {
        navigate("/exams/panel");
    };

    // ── Question analytics, grouped subject → topic, loaded per group ──────────
    // The page ships only the collapsed headers with their whole-group totals; a topic's question rows
    // are fetched the first time it is opened. A teacher sees which topic the cohort struggled with at
    // a glance, and only pays for the questions they actually want to read.
    const {expanded: analysisExpanded, items: analysisItems, loading: analysisLoading,
        errors: analysisErrors, toggle: toggleAnalysisGroup} = useLazyGroups(
        (group) => getQuestionAnalysisGroup(examId, {subjectId: group.subjectId, topicId: group.topicId})
    );

    // Nest the flat group list under one header per subject, preserving the backend's ordering.
    const analysisSubjects = useMemo(() => {
        const list = Array.isArray(analysisGroups) ? analysisGroups : [];
        const subjects = [];
        const indexByKey = new Map();
        list.forEach((group) => {
            const key = group.subjectId != null ? `s${group.subjectId}` : group.subjectName || "__none__";
            if (!indexByKey.has(key)) {
                indexByKey.set(key, subjects.length);
                subjects.push({key, subjectName: group.subjectName, topics: []});
            }
            subjects[indexByKey.get(key)].topics.push(group);
        });
        return subjects;
    }, [analysisGroups]);

    const analysisQuestionTotal = useMemo(
        () => (analysisGroups || []).reduce((sum, g) => sum + (Number(g.questionCount) || 0), 0),
        [analysisGroups]
    );

    // Group accuracy comes from the raw counts (not an average of per-question rates), so a question
    // everyone answered weighs as much as its answer count deserves.
    const renderGroupStats = ({questionCount, correctCount, totalAnswers, correctRate}) => {
        const rate = correctRate != null ? Number(correctRate) : null;
        return (
            <span className={styles.groupStats}>
                <span>{t("pages.examDetail.groupQuestionCount", {count: Number(questionCount) || 0})}</span>
                <span>{correctCount ?? 0} / {totalAnswers ?? 0}</span>
                <span className={rate !== null && rate < 50 ? styles.groupRateLow : styles.groupRate}>
                    {rate !== null ? `${rate} %` : "-"}
                </span>
            </span>
        );
    };

    // A subject header shows the sum of its topics — still whole-group numbers, just added up.
    const sumTopics = (topics) => ({
        questionCount: topics.reduce((s, g) => s + (Number(g.questionCount) || 0), 0),
        correctCount: topics.reduce((s, g) => s + (Number(g.correctCount) || 0), 0),
        totalAnswers: topics.reduce((s, g) => s + (Number(g.totalAnswers) || 0), 0),
        get correctRate() {
            return this.totalAnswers > 0
                ? Math.round((this.correctCount * 1000) / this.totalAnswers) / 10
                : null;
        },
    });

    const renderAnalysisRow = (qa) => {
        const difficulty = getDifficulty(qa.difficultyLevel);
        return (
            <tr key={qa.questionId}>
                <td>{truncate(qa.questionText, 40)}</td>
                <td>
                    <span className={`${styles.difficultyBadge} ${styles[difficulty?.name?.toLowerCase()]}`}>
                        {difficulty?.label || ""}
                    </span>
                </td>
                <td>{qa.correctCount}</td>
                <td>{qa.totalAnswers}</td>
                <td>{qa.correctRate} %</td>
            </tr>
        );
    };


    return (
        <div className={styles.container}>
            <header className={styles.topBar}>
                <button type="button" onClick={goBack} className={styles.backBtn}>
                    <FiArrowLeft size={18}/>
                    <span>{t("pages.examDetail.backToPanel")}</span>
                </button>
            </header>

            <PageHeader
                title={exam?.examName || t("pages.examDetail.title")}
                subtitle={t("pages.examDetail.subtitle")}
            />

            {loading ? (
                <Loader fullscreen/>
            ) : error ? (
                <div className={styles.errorCard}>
                    <p className={styles.errorText}>{error}</p>
                </div>
            ) : !exam ? (
                <div className={styles.emptyCard}>
                    <p className={styles.emptyText}>{t("pages.examDetail.notFound")}</p>
                </div>
            ) : (
                <>
                    <div className={styles.hero}>
                        <div className={styles.heroContent}>
                            <div className={styles.heroBadge}>{renderStatusBadge(exam.status)}</div>
                            <p className={styles.heroSub}>
                                {t("pages.examDetail.startEnd", { start: formatDateTime(exam.scheduledStartTime), end: formatDateTime(exam.scheduledEndTime) })}
                            </p>
                        </div>
                        {!isStudent && exam.status === "STARTED" && (
                            <button
                                type="button"
                                className={styles.confirmBtn}
                                onClick={() => setShowFinishModal(true)}
                                disabled={finishing}
                                title={t("pages.examDetail.finishExamTitle", {defaultValue: "İmtahanı indi bitir"})}
                            >
                                <FiStopCircle/>
                                <span>
                                    {finishing
                                        ? t("pages.examDetail.finishing", {defaultValue: "Bitirilir..."})
                                        : t("pages.examDetail.finishExam", {defaultValue: "İmtahanı bitir"})}
                                </span>
                            </button>
                        )}
                    </div>

                    <div className={styles.cardsGrid}>
                        <section className={styles.card}>
                            <div className={styles.cardIconWrap}>
                                <FiCalendar className={styles.cardIcon}/>
                            </div>
                            <h3 className={styles.cardTitle}>{t("pages.examDetail.examInfo")}</h3>
                            <ul className={styles.cardList}>
                                <li className={styles.cardItem}>
                                    <span className={styles.cardLabel}>{t("pages.examDetail.name")}</span>
                                    <span className={styles.cardValue}>{exam.examName}</span>
                                </li>
                                <li className={`${styles.cardItem} ${examBase?.multiSubject ? styles.cardItemStacked : ""}`}>
                                    <span className={styles.cardLabel}>{t("pages.examDetail.subject")}</span>
                                    {examBase?.multiSubject ? (
                                        <div className={styles.subjectChips}>
                                            {(examBase.subjects || []).map((s) => (
                                                <span key={s.id} className={styles.subjectChip}>{s.title}</span>
                                            ))}
                                        </div>
                                    ) : (
                                        <span className={styles.cardValue}>{examBase?.subjectTitle || "-"}</span>
                                    )}
                                </li>
                                <li className={styles.cardItem}>
                                    <span className={styles.cardLabel}>{t("pages.examDetail.duration")}</span>
                                    <span className={styles.cardValue}>
                                        <FiClock size={14} style={{verticalAlign: "middle", marginRight: 4}}/>
                                        {exam.durationMinutes}
                                    </span>
                                </li>
                                <li className={styles.cardItem}>
                                    <span className={styles.cardLabel}>{t("pages.examDetail.passPercent")}</span>
                                    <span className={styles.cardValue}>
                                        <FiPercent size={14} style={{verticalAlign: "middle", marginRight: 4}}/>
                                        {exam.passRate}
                                    </span>
                                </li>
                                <li className={styles.cardItem}>
                                    <span className={styles.cardLabel}>{t("pages.examDetail.status")}</span>
                                    <span className={styles.cardValue}>
                                        {renderStatusBadge(exam.status)}
                                    </span>
                                </li>
                                <li className={styles.cardItem}>
                                    <span className={styles.cardLabel}>{t("pages.examDetail.createdBy")}</span>
                                    <span className={styles.cardValue}>{exam.createdBy || "-"}</span>
                                </li>
                                <li className={styles.cardItem}>
                                    <span className={styles.cardLabel}>{t("pages.examDetail.note")}</span>
                                    <span className={styles.cardValue}>{exam.note || "-"}</span>
                                </li>
                            </ul>
                        </section>
                        <section className={styles.card}>
                            <div className={styles.cardIconWrap}>
                                <FiList className={styles.cardIcon}/>
                            </div>
                            <h3 className={styles.cardTitle}>{t("pages.examDetail.result")}</h3>
                            <ul className={styles.cardList}>
                                <li className={styles.cardItem}>
                                    <span className={styles.cardLabel}>{t("pages.examDetail.participantCount")}</span>
                                    <span className={styles.cardValue}>{examSummary?.totalStudents ?? "-"}</span>
                                </li>
                                <li className={styles.cardItem}>
                                    <span className={styles.cardLabel}>{t("pages.examDetail.maxScore")}</span>
                                    <span className={styles.cardValue}>
                                        <FiPercent size={14} style={{verticalAlign: "middle", marginRight: 4}}/>
                                        {formatScore(examSummary?.maxScore)}
                                    </span>
                                </li>
                                <li className={styles.cardItem}>
                                    <span className={styles.cardLabel}>{t("pages.examDetail.minScore")}</span>
                                    <span className={styles.cardValue}>
                                        <FiPercent size={14} style={{verticalAlign: "middle", marginRight: 4}}/>
                                        {formatScore(examSummary?.minScore)}
                                    </span>
                                </li>
                                <li className={styles.cardItem}>
                                    <span className={styles.cardLabel}>{t("pages.examDetail.avgScore")}</span>
                                    <span className={styles.cardValue}>
                                        <FiPercent size={14} style={{verticalAlign: "middle", marginRight: 4}}/>
                                        {formatScore(examSummary?.avgScore)}
                                    </span>
                                </li>
                            </ul>
                        </section>
                    </div>

                    <section className={styles.studentsSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionTitleWrap}>
                                <FiUser className={styles.sectionIcon}/>
                                <h3 className={styles.sectionTitle}>{t("pages.examDetail.studentsTitle")}</h3>
                                {/* Assigned total for the whole exam, not the size of this page. */}
                                <span className={styles.sectionCount}>{studentCount}</span>
                            </div>
                        </div>

                        {studentCount === 0 ? (
                            <div className={styles.emptyWrapper}>
                                <TableEmptyState
                                    title={t("pages.examDetail.noStudentsTitle")}
                                    description={t("pages.examDetail.noStudentsDescription")}
                                />
                            </div>
                        ) : (
                            <div className={styles.tableCard}>
                                <div className={styles.tableScroll}>
                                    <table className={styles.table}>
                                        <thead>
                                        <tr>
                                            <th>{t("pages.students.studentNumber")}</th>
                                            <th>{t("pages.students.firstName")}</th>
                                            <th>{t("pages.students.lastName")}</th>
                                            <th>{t("common.email")}</th>
                                            <th>{t("common.phone")}</th>
                                            <th>{t("common.address")}</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {students.map((s) => (
                                            <tr key={s.studentNumber} style={s.disabled ? {opacity: 0.55} : undefined}>
                                                <td>
                                                    <span className={styles.studentNum}>{s.studentNumber}</span>
                                                </td>
                                                <td>
                                                    {s.firstName}
                                                    {s.disabled && (
                                                        <span style={{marginLeft: 8, padding: "2px 8px", borderRadius: 999, background: "#fee2e2", color: "#b91c1c", fontSize: 11, fontWeight: 600, verticalAlign: "middle"}}>
                                                            {t("pages.students.disabledBadge")}
                                                        </span>
                                                    )}
                                                </td>
                                                <td>{s.lastName}</td>
                                                <td>{s.email}</td>
                                                <td>{s.phone || "-"}</td>
                                                <td>{s.address || "-"}</td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </div>
                                <TablePager
                                    page={studentsPage}
                                    totalPages={studentsTotalPages}
                                    onChange={setStudentsPage}
                                    busy={studentsLoading}
                                    t={t}
                                />
                            </div>
                        )}
                    </section>


                    <section className={styles.studentsSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.attemptsSectionTitleWrap}>
                                <div className={styles.sectionTitleHeader}>
                                    <FiList className={styles.sectionIcon}/>
                                    <h3 className={styles.sectionTitle}>{t("pages.examAttempts.title")}</h3>
                                    {/* Attempt total across the exam, not this page's row count. */}
                                    <span className={styles.sectionCount}>{attemptsTotal}</span>
                                </div>
                                <div style={{display: "flex", gap: 8, marginLeft: "auto"}}>
                                    <button
                                        type="button"
                                        className={styles.confirmBtn}
                                        onClick={handleExportAttempts}
                                        disabled={exporting || attemptsTotal === 0}
                                        title={t("pages.examAttempts.exportTitle", {defaultValue: "Excel olaraq endir"})}
                                    >
                                        <FiDownload/>
                                        <span>
                                            {exporting
                                                ? t("pages.examAttempts.exporting", {defaultValue: "Endirilir..."})
                                                : t("pages.examAttempts.exportExcel", {defaultValue: "Excel"})}
                                        </span>
                                    </button>
                                    {canBeConfirmedAll() &&
                                        <button
                                            type="button"
                                            className={styles.confirmBtn}
                                            onClick={() => setShowConfirmResultModal(true)}
                                            disabled={confirming}
                                        >
                                            <FiCheckCircle/>
                                            <span>{confirming ? t("pages.examDetail.confirming") : t("pages.examDetail.confirmAllResults")}</span>
                                        </button>
                                    }
                                </div>
                            </div>
                        </div>

                        <div className={styles.tableCard}>
                            <div className={styles.tableScroll}>
                                <table className={styles.table}>
                                    <thead>
                                    <tr>
                                        <th>{t("pages.examDetail.tableNo")}</th>
                                        <th>{t("pages.examAttempts.colStudent")}</th>
                                        <th>{t("pages.examDetail.result")}</th>
                                        <th>{t("pages.examAttempts.colScore")}</th>
                                        <th>{t("pages.examAttempts.colCorrectTotal")}</th>
                                        <th>{t("pages.examDetail.duration")}</th>
                                        <th>{t("pages.examAttempts.colSuspicion")}</th>
                                        <th>{t("pages.examAttempts.colDecision")}</th>
                                        <th>{t("pages.examAttempts.colLastActivity")}</th>
                                        <th>{t("common.details")}</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {attempts.map((attempt) => {
                                        const key = attempt.attemptId ?? attempt.id;
                                        const score = attempt.score !== undefined && attempt.score !== null ? attempt.score : "-"
                                        const result = attempt.correct !== undefined && attempt.total !== undefined ? `${attempt.correct} / ${attempt.total}` : "-"

                                        return (
                                            <tr key={key} style={attempt.studentDisabled ? {opacity: 0.55} : undefined}>
                                                <td>{attempt.rank}</td>
                                                <td>
                                                    {attempt.studentName || "-"}
                                                    {attempt.studentDisabled && (
                                                        <span style={{marginLeft: 8, padding: "2px 8px", borderRadius: 999, background: "#fee2e2", color: "#b91c1c", fontSize: 11, fontWeight: 600, verticalAlign: "middle"}}>
                                                            {t("pages.students.disabledBadge")}
                                                        </span>
                                                    )}
                                                </td>
                                                <td>{renderStudentExamResultStatusBadge(attempt.studentExamResultStatus)}</td>
                                                <td>{score} %</td>
                                                <td>{result}</td>
                                                <td>{formatDuration(attempt.spentSeconds)}</td>
                                                <td>{renderFlagBadge(attempt.flaggedForSuspicion)}</td>
                                                <td>{renderAttemptStatusBadge(attempt.attemptStatus, attempt.status)}</td>
                                                <td>{renderLastSeen(attempt)}</td>
                                                <td>
                                                    <button type="button" className={styles.iconButton} onClick={() =>
                                                        navigate(`/exam-attempts/${key}/detail`, {
                                                            state: {
                                                                attemptStatus: attempt.attemptStatus,
                                                                myStatus: attempt.myStatus,
                                                                finalStatus: attempt.studentExamResultStatus,
                                                                status: attempt.status,
                                                                score: score,
                                                                result: result,
                                                                cancelResultReason: attempt.cancelResultReason
                                                            },
                                                        })
                                                    }
                                                    >
                                                        <FiEye/>
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    </tbody>
                                </table>
                            </div>
                            <TablePager
                                page={attemptsPage}
                                totalPages={attemptsTotalPages}
                                onChange={setAttemptsPage}
                                busy={attemptsLoading}
                                t={t}
                            />
                        </div>
                    </section>

                    <section className={styles.studentsSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.attemptsSectionTitleWrap}>
                                <div className={styles.sectionTitleHeader}>
                                    <BsFillQuestionCircleFill className={styles.sectionIcon}/>
                                    <h3 className={styles.sectionTitle}>{t("pages.examDetail.questionAnalytics")}</h3>
                                </div>
                                <button
                                    type="button"
                                    className={styles.confirmBtn}
                                    onClick={handleExportQuestionAnalysis}
                                    disabled={exportingQA || analysisQuestionTotal === 0}
                                    style={{marginLeft: "auto"}}
                                    title={t("pages.examAttempts.exportTitle", {defaultValue: "Excel olaraq endir"})}
                                >
                                    <FiDownload/>
                                    <span>
                                        {exportingQA
                                            ? t("pages.examAttempts.exporting", {defaultValue: "Endirilir..."})
                                            : t("pages.examAttempts.exportExcel", {defaultValue: "Excel"})}
                                    </span>
                                </button>
                            </div>
                        </div>

                        <div className={styles.tableCard}>
                            <div className={styles.tableScroll}>
                                <table className={styles.table}>
                                    <thead>
                                    <tr>
                                        <th>{t("pages.examDetail.tableQuestion")}</th>
                                        <th>{t("pages.examDetail.difficulty")}</th>
                                        <th>{t("pages.examDetail.correctCount")}</th>
                                        <th>{t("pages.examDetail.tableTotalAnswers")}</th>
                                        <th>{t("pages.examDetail.overallAccuracy")}</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {analysisSubjects.map((subject) => (
                                        <React.Fragment key={subject.key}>
                                            <tr className={styles.subjectGroupRow}>
                                                <td colSpan={5}>
                                                    <div className={styles.groupHeader}>
                                                        <span className={styles.groupName}>
                                                            {subject.subjectName || t("pages.examDetail.otherSubject")}
                                                        </span>
                                                        {renderGroupStats(sumTopics(subject.topics))}
                                                    </div>
                                                </td>
                                            </tr>
                                            {subject.topics.map((topic) => {
                                                const key = groupKey(topic);
                                                const isOpen = !!analysisExpanded[key];
                                                const rows = analysisItems[key];
                                                return (
                                                    <React.Fragment key={key}>
                                                        <tr
                                                            className={`${styles.topicGroupRow} ${styles.clickableGroupRow}`}
                                                            onClick={() => toggleAnalysisGroup(topic)}
                                                            role="button"
                                                            tabIndex={0}
                                                            aria-expanded={isOpen}
                                                            onKeyDown={(e) => {
                                                                if (e.key === "Enter" || e.key === " ") {
                                                                    e.preventDefault();
                                                                    toggleAnalysisGroup(topic);
                                                                }
                                                            }}
                                                        >
                                                            <td colSpan={5}>
                                                                <div className={styles.groupHeader}>
                                                                    <span className={styles.groupName}>
                                                                        <span className={styles.groupChevron}>
                                                                            {isOpen ? <FiChevronDown size={14}/> : <FiChevronRight size={14}/>}
                                                                        </span>
                                                                        {topic.topicName || t("pages.examDetail.otherTopic")}
                                                                    </span>
                                                                    {renderGroupStats(topic)}
                                                                </div>
                                                            </td>
                                                        </tr>

                                                        {isOpen && analysisLoading[key] && (
                                                            <tr>
                                                                <td colSpan={5} className={styles.groupLoadingCell}>
                                                                    {t("common.loading", {defaultValue: "Yüklənir..."})}
                                                                </td>
                                                            </tr>
                                                        )}
                                                        {isOpen && analysisErrors[key] && (
                                                            <tr>
                                                                <td colSpan={5} className={styles.groupErrorCell}>
                                                                    {t("pages.examDetail.groupLoadError")}
                                                                </td>
                                                            </tr>
                                                        )}
                                                        {isOpen && rows && rows.map(renderAnalysisRow)}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </React.Fragment>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </section>

                </>
            )}
            <ConfirmCancelModal
                isOpen={showConfirmResultModal}
                message={t("pages.examDetail.confirmAllMessage")}
                confirmText={t("common.confirm")}
                onConfirm={onConfirm}
                onCancel={() => setShowConfirmResultModal(false)}
                disabled={confirming}
            />
            <ConfirmActionModal
                isOpen={showFinishModal}
                title={t("pages.examDetail.finishExamTitle", {defaultValue: "İmtahanı bitirmək?"})}
                message={t("pages.examDetail.finishExamMessage", {defaultValue: "İmtahan dərhal bitiriləcək və davam edən cəhdlər avtomatik təhvil veriləcək. Davam etmək istəyirsiniz?"})}
                confirmLabel={t("pages.examDetail.finishExam", {defaultValue: "İmtahanı bitir"})}
                cancelLabel={t("common.cancel")}
                loadingLabel={t("pages.examDetail.finishing", {defaultValue: "Bitirilir..."})}
                loading={finishing}
                tone="danger"
                onConfirm={handleFinishExam}
                onCancel={() => setShowFinishModal(false)}
            />
        </div>
    );
};

export default ExamDetailPage;
